import AsyncStorage from '@react-native-async-storage/async-storage';
import { netState } from './common';

const OFFLINE_QUEUE_KEY = 'pos_offline_order_queue';

interface QueuedOrder {
  id: string;
  timestamp: number;
  order: any;
  /** company:user that queued it - a different login on this device must not sync it */
  owner?: string;
  retryCount: number;
  /** reason the last sync attempt was rejected by the server (e.g. excess stock limit) */
  lastError?: string;
}

/**
 * Get all queued offline orders
 */
export const getOfflineQueue = async (): Promise<QueuedOrder[]> => {
  try {
    const data = await AsyncStorage.getItem(OFFLINE_QUEUE_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
};

/**
 * Add an order to the offline queue when network is unavailable
 */
export const enqueueOrder = async (order: any, owner?: string): Promise<string> => {
  const queue = await getOfflineQueue();
  // letters, digits and hyphens only: the same id doubles as the server Idempotency-Key
  const queueId = `offline-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  queue.push({
    id: queueId,
    timestamp: Date.now(),
    order,
    owner,
    retryCount: 0,
  });
  await AsyncStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
  return queueId;
};

/**
 * Remove a successfully synced order from the queue
 */
export const dequeueOrder = async (queueId: string): Promise<void> => {
  const queue = await getOfflineQueue();
  const filtered = queue.filter(item => item.id !== queueId);
  await AsyncStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(filtered));
};

/**
 * Increment retry count for a failed sync
 */
const markRetry = async (queueId: string, error?: string): Promise<void> => {
  const queue = await getOfflineQueue();
  const item = queue.find(q => q.id === queueId);
  if (item) {
    item.retryCount += 1;
    if (error) item.lastError = error;
  }
  await AsyncStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
};

/**
 * Sync all queued orders with the server.
 * Call this when network comes back online.
 * Returns { synced: number, failed: number }
 */
let syncInFlight = false;

export const syncOfflineQueue = async (
  insertFn: (order: any, idempotencyKey: string) => Promise<any>,
  owner?: string,
): Promise<{ synced: number; failed: number; lastError?: string }> => {
  // One sync at a time: NetInfo events and screen mounts can fire together
  if (syncInFlight) return { synced: 0, failed: 0 };
  syncInFlight = true;
  try {
    return await runSync(insertFn, owner);
  } finally {
    syncInFlight = false;
  }
};

const runSync = async (
  insertFn: (order: any, idempotencyKey: string) => Promise<any>,
  owner?: string,
): Promise<{ synced: number; failed: number; lastError?: string }> => {
  const isOnline = await netState();
  if (!isOnline) return { synced: 0, failed: 0 };

  // Only this login's orders; items queued before owners were recorded have none and are kept for anyone
  const queue = (await getOfflineQueue()).filter(
    item => !item.owner || !owner || item.owner === owner,
  );
  if (queue.length === 0) return { synced: 0, failed: 0 };

  let synced = 0;
  let failed = 0;
  let lastError: string | undefined;
  const MAX_RETRIES = 3;

  for (const item of queue) {
    if (item.retryCount >= MAX_RETRIES) {
      failed++;
      continue;
    }
    try {
      // Same key on every retry: if the server saved the order but the reply was lost, it answers with the saved one
      await insertFn(item.order, item.id.replace(/_/g, '-'));
      await dequeueOrder(item.id);
      synced++;
    } catch (e: any) {
      lastError = e?.data?.message || e?.data?.error || e?.message || 'Order was rejected';
      await markRetry(item.id, lastError);
      failed++;
    }
  }

  return { synced, failed, lastError };
};

/**
 * Orders the server keeps rejecting (retries used up). They stay in the queue with the reason
 * so the cashier can see why; discard them with dequeueOrder() or retry with resetRetries().
 */
export const getFailedOrders = async (): Promise<QueuedOrder[]> => {
  const queue = await getOfflineQueue();
  return queue.filter(item => item.retryCount >= 3);
};

/** Give a failed order another set of sync attempts (e.g. after fixing stock) */
export const resetRetries = async (queueId: string): Promise<void> => {
  const queue = await getOfflineQueue();
  const item = queue.find(q => q.id === queueId);
  if (item) {
    item.retryCount = 0;
    await AsyncStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
  }
};

/**
 * Check if there are pending offline orders
 */
export const hasPendingOrders = async (): Promise<boolean> => {
  const queue = await getOfflineQueue();
  return queue.length > 0;
};

/**
 * Get count of pending offline orders
 */
export const getPendingCount = async (): Promise<number> => {
  const queue = await getOfflineQueue();
  return queue.length;
};

/**
 * Clear all offline orders (use after user confirms discard)
 */
export const clearOfflineQueue = async (): Promise<void> => {
  await AsyncStorage.removeItem(OFFLINE_QUEUE_KEY);
};
